// Shared with rea-cart-bundle-breakdown.js: given a bundle's variants (each
// {id, available, options: string[]}) and a target combination of option
// values (same order as the bundle's own option list), find the variant
// that offers exactly that combination. Falls back to the bundle's first
// available variant if nothing matches exactly, rather than blocking
// whatever action needed a variant id - both callers treat "the requested
// combination isn't real" the same way (no basis to guess further, so pick
// something valid rather than nothing at all).
window.reaFindBundleVariant =
  window.reaFindBundleVariant ||
  function (bundleVariants, targetValues) {
    const exactMatch = bundleVariants.find(
      (v) => v.options.length === targetValues.length && v.options.every((val, i) => val === targetValues[i])
    );
    if (exactMatch) return exactMatch.id;

    console.warn('[rea-bundle-variant-match] no exact variant match for', targetValues, '- using fallback variant');
    const fallback = bundleVariants.find((v) => v.available) || bundleVariants[0];
    return fallback ? fallback.id : null;
  };

if (!window.reaCartBundleUpsellInit) {
  window.reaCartBundleUpsellInit = true;

  // Given a bundle's option names/variants and the cart line item being
  // replaced, pick the bundle variant to add.
  //
  // Every bundle in this store has at most one option per multi-size
  // component: one explicitly named after that component (e.g. "Soleil
  // Levant Top (Size)") for every component except whichever one was set
  // up first in the Bundles app, which keeps the bare default option name
  // ("Size") with no product name in it. The item being replaced gives a
  // known-correct size value for whichever option corresponds to its own
  // product; every OTHER size option in the bundle (the bare "Size" case,
  // or another component entirely) has no direct signal from it, so it's
  // defaulted to that same value too, on the judgment call that a customer
  // buying a set is more likely than not to wear a consistent size across
  // its pieces. Both cases end up wanting the same value, so there's no
  // per-option name-matching left to actually do here - only the option
  // *count* matters, to match the shape of each variant's own options
  // array. If the resulting combination isn't an offered variant (e.g. the
  // reused size doesn't exist on the other component), that's a real
  // "no basis to guess further" case, handled by reaFindBundleVariant
  // falling back to the bundle's first available variant rather than
  // blocking the swap.
  function pickBundleVariantId(bundleOptionNames, bundleVariants, originalOptions) {
    const referenceValue = originalOptions.length ? originalOptions[0].value : null;

    if (referenceValue == null) {
      // No size info at all to carry over (e.g. the replaced item is a
      // "One Size" piece with nothing to match) - no basis to prefer one
      // variant over another, so just take the bundle's first available one.
      const fallback = bundleVariants.find((v) => v.available) || bundleVariants[0];
      return fallback ? fallback.id : null;
    }

    const targetValues = bundleOptionNames.map(() => referenceValue);
    return window.reaFindBundleVariant(bundleVariants, targetValues);
  }

  function readButtonData(button) {
    return {
      originalLineKey: button.dataset.originalLineKey,
      originalOptions: JSON.parse(button.dataset.originalOptions || '[]'),
      bundleOptionNames: JSON.parse(button.dataset.bundleOptions || '[]'),
      bundleVariants: JSON.parse(button.dataset.bundleVariants || '[]'),
    };
  }

  async function performSwap(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('loading');

    const data = readButtonData(button);
    const variantId = pickBundleVariantId(data.bundleOptionNames, data.bundleVariants, data.originalOptions);

    if (!variantId) {
      console.error('[rea-cart-bundle-upsell] could not determine a bundle variant to add');
      button.disabled = false;
      button.classList.remove('loading');
      return;
    }

    let addResponse;
    try {
      const res = await fetch(routes.cart_add_url, {
        ...fetchConfig('json'),
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      addResponse = await res.json();
    } catch (error) {
      console.error('[rea-cart-bundle-upsell] add-to-cart request failed', error);
      button.disabled = false;
      button.classList.remove('loading');
      return;
    }

    if (addResponse.status) {
      // Add itself failed (e.g. sold out) - nothing changed yet, safe to
      // just report and leave the cart untouched.
      console.error(
        '[rea-cart-bundle-upsell] add-to-cart error',
        addResponse.description || addResponse.message
      );
      button.disabled = false;
      button.classList.remove('loading');
      return;
    }

    try {
      const res = await fetch(routes.cart_change_url, {
        ...fetchConfig('json'),
        body: JSON.stringify({ id: data.originalLineKey, quantity: 0 }),
      });
      const removeResponse = await res.json();
      if (removeResponse.status || removeResponse.errors) {
        throw new Error(removeResponse.description || removeResponse.message || 'remove failed');
      }
    } catch (error) {
      // Bundle add succeeded but removing the original line didn't - roll
      // back the add (by the amount this action just added, not the whole
      // line, in case the bundle was already in the cart beforehand) so
      // the cart isn't left with both items duplicated, rather than
      // leaving a half-finished swap for the customer to notice at checkout.
      console.error(
        '[rea-cart-bundle-upsell] removing the original item failed after the bundle was added - rolling back',
        error
      );
      try {
        await fetch(routes.cart_change_url, {
          ...fetchConfig('json'),
          body: JSON.stringify({ id: addResponse.key, quantity: addResponse.quantity - 1 }),
        });
      } catch (rollbackError) {
        console.error(
          '[rea-cart-bundle-upsell] rollback also failed - cart may be in an inconsistent state, refreshing to show actual server state',
          rollbackError
        );
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-upsell' });
      button.disabled = false;
      button.classList.remove('loading');
      return;
    }

    // Full success - the drawer's own cartUpdate subscribers (cart-items'
    // onCartUpdate, plus rea-cart-drawer-refresh.js's shipping-bar/
    // recommendations refresh) re-render everything from a fresh section
    // fetch, this card included, so nothing further to patch here.
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-upsell' });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-bundle-upsell-swap]');
    if (!button) return;
    performSwap(button);
  });
}
